import { useMemo } from "react";
import { Flex, Typography } from "antd";
import type { ColumnsType, TableProps } from "antd/es/table";
import { useTranslate } from "@refinedev/core";
import dayjs from "dayjs";
import { MediaProjectListItem } from "../../types";
import { ProjectSessionSummary } from "../../home/hooks/useOrganizationAnalytics";
import { ProjectStatusTag } from "../../projects/components/ProjectStatusTag";
import { ProjectTypeTag } from "../../projects/components/ProjectType";
import { isProjectActionNeeded } from "../../projects/helpers/helper";
import { formatPct } from "../../components/analytics/utils/";
import { toNumber, formatSeenDay } from "./utils";
import { PercentBar } from "./PercentBar";
import {
  EXPERIENCE_COLUMNS,
  type ExperienceColumnKey,
} from "./experienceColumns";
import { StyledExperiencesTable } from "helpers/table/StyledExperiencesTable";
import { TableHeader } from "helpers/table/TableHeader";
import { centeredPagination } from "helpers/table/tableUtils";

export type ExperiencesTableProps = {
  tableProps: TableProps<MediaProjectListItem>;
  summariesMap: Map<string, ProjectSessionSummary>;
  visibleColumns: ExperienceColumnKey[];
  onExperienceClick?: (record: MediaProjectListItem) => void;
};

export const ExperiencesTable = ({
  tableProps,
  summariesMap,
  visibleColumns,
  onExperienceClick,
}: ExperiencesTableProps) => {
  const t = useTranslate();
  // "Action needed" projects (StoryboardReviewing / WaitingForSettings) are
  // unfinished and can't have analytics, so drop them from the list.
  const dataSource = useMemo(
    () =>
      (tableProps.dataSource ?? []).filter(
        (record) => !isProjectActionNeeded(record.status)
      ),
    [tableProps.dataSource]
  );

  const columnsByKey = useMemo<
    Record<ExperienceColumnKey, ColumnsType<MediaProjectListItem>[number]>
  >(
    () => ({
      title: {
        // key must match the BE `sort_by` field (Refine sends the column key as
        // sort_by) — allowed: title, status, type, created, updated.
        key: "title",
        title: <TableHeader title={t("media.analytics.columns.name")} />,
        dataIndex: "title",
        sorter: true,
        ellipsis: true,
        render: (_: unknown, record) => (
          <Flex gap={12}>
            <Flex vertical gap={2}>
              <Typography.Text strong ellipsis>
                {record.title}
              </Typography.Text>
              {record.tags && record.tags.length > 0 && (
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: 12 }}
                  ellipsis
                >
                  {record.tags.map((tag) => tag.tag_value).join(" · ")}
                </Typography.Text>
              )}
            </Flex>
          </Flex>
        ),
      },
      type: {
        key: "type",
        title: <TableHeader title={t("media.analytics.columns.type")} />,
        dataIndex: "type",
        sorter: true,
        width: 130,
        render: (_: unknown, record) => <ProjectTypeTag type={record.type} />,
      },
      lastViewed: {
        key: "lastViewed",
        title: <TableHeader title={t("media.analytics.columns.lastViewed")} />,
        width: 160,
        // `last_seen_date` is the last day the experience actually had a viewing
        // session.
        render: (_: unknown, record) => (
          <Typography.Text type="secondary">
            {formatSeenDay(summariesMap.get(record.id)?.last_seen_date, t)}
          </Typography.Text>
        ),
      },
      firstSeen: {
        key: "firstSeen",
        title: <TableHeader title={t("media.analytics.columns.firstSeen")} />,
        width: 160,
        // First day the experience had a viewing session.
        render: (_: unknown, record) => (
          <Typography.Text type="secondary">
            {formatSeenDay(summariesMap.get(record.id)?.first_seen_date, t)}
          </Typography.Text>
        ),
      },
      lastModified: {
        key: "updated",
        title: (
          <TableHeader title={t("media.analytics.columns.lastModified")} />
        ),
        dataIndex: "updated",
        sorter: true,
        width: 160,
        // Project's `updated` time.
        render: (_: unknown, record) => (
          <Typography.Text type="secondary">
            {record.updated ? dayjs(record.updated).fromNow() : "—"}
          </Typography.Text>
        ),
      },
      views: {
        key: "views",
        title: <TableHeader title={t("media.analytics.columns.views")} />,
        align: "center",
        width: 100,
        render: (_: unknown, record) => (
          <Typography.Text type="secondary">
            {summariesMap.get(record.id)?.impression_sessions ?? 0}
          </Typography.Text>
        ),
      },
      playSessions: {
        key: "playSessions",
        title: (
          <TableHeader title={t("media.analytics.columns.playSessions")} />
        ),
        align: "center",
        width: 110,
        render: (_: unknown, record) => (
          <Typography.Text>
            {summariesMap.get(record.id)?.play_sessions ?? 0}
          </Typography.Text>
        ),
      },
      documentSessions: {
        key: "documentSessions",
        title: (
          <TableHeader title={t("media.analytics.columns.documentSessions")} />
        ),
        align: "center",
        width: 120,
        render: (_: unknown, record) => (
          <Typography.Text>
            {summariesMap.get(record.id)?.document_sessions ?? 0}
          </Typography.Text>
        ),
      },
      readDepth: {
        key: "readDepth",
        title: <TableHeader title={t("media.analytics.columns.readDepth")} />,
        width: 200,
        render: (_: unknown, record) => {
          const pct = toNumber(
            summariesMap.get(record.id)?.document_reading_depth_pct
          );
          if (pct == null) {
            return <Typography.Text>—</Typography.Text>;
          }
          return <PercentBar percent={pct} />;
        },
      },
      chat: {
        key: "chat",
        title: <TableHeader title={t("media.analytics.columns.chat")} />,
        align: "center",
        width: 130,
        render: (_: unknown, record) => {
          const count = summariesMap.get(record.id)?.chat_sessions ?? 0;
          return <Typography.Text>{count}</Typography.Text>;
        },
      },
      chatRate: {
        key: "chatRate",
        title: <TableHeader title={t("media.analytics.columns.chatRate")} />,
        align: "center",
        width: 120,
        // Share of sessions that chatted — richer than the raw chat count.
        render: (_: unknown, record) => {
          const pct = toNumber(summariesMap.get(record.id)?.chat_rate_pct);
          return <Typography.Text>{formatPct(pct)}</Typography.Text>;
        },
      },
      videoCompletion: {
        key: "videoCompletion",
        title: (
          <TableHeader title={t("media.analytics.columns.videoCompletion")} />
        ),
        align: "center",
        width: 200,
        render: (_: unknown, record) => {
          const pct = toNumber(
            summariesMap.get(record.id)?.video_completion_pct
          );
          if (pct == null) {
            return <Typography.Text>—</Typography.Text>;
          }
          return <Typography.Text>{formatPct(pct)}</Typography.Text>;
        },
      },
      status: {
        key: "status",
        title: <TableHeader title={t("media.analytics.columns.status")} />,
        dataIndex: "status",
        sorter: true,
        width: 150,
        render: (_: unknown, record) => (
          <ProjectStatusTag status={record.status} />
        ),
      },
    }),
    [t, summariesMap]
  );

  // Render only the selected columns, in the canonical registry order so the
  // layout stays stable regardless of the order keys were toggled in.
  const columns: ColumnsType<MediaProjectListItem> = useMemo(
    () =>
      EXPERIENCE_COLUMNS.filter((c) => visibleColumns.includes(c.key)).map(
        (c) => columnsByKey[c.key]
      ),
    [columnsByKey, visibleColumns]
  );

  return (
    <StyledExperiencesTable
      {...tableProps}
      dataSource={dataSource}
      pagination={centeredPagination(tableProps.pagination)}
      rowKey="id"
      columns={columns}
      onRow={(record) => ({
        onClick: () => onExperienceClick?.(record),
        style: onExperienceClick ? { cursor: "pointer" } : undefined,
      })}
      locale={{ emptyText: t("media.analytics.empty") }}
    />
  );
};
